import React, { useState, useEffect } from 'react';
import { Button, Tooltip } from 'antd';
import MapPanel from '../../features/data/panels/Map/MapPanelContainer';
import { BorderInnerOutlined } from '@ant-design/icons';
import styles from './DashboardView.module.css';
import PlotsPanel from '../../features/data/panels/Plots/PlotsPanel';
import CombatTrackerContainer from '../CombatTracker/CombatTrackerContainer';
import type { RenderedImageRect } from '../../types/types';
import NewsTicker from '../../features/dashboard/panels/NewsTicker/NewsTicker';
import { PanelErrorBoundary } from './PanelErrorBoundary';
import WorldStatusPanelContainer from '../../features/dashboard/panels/WorldStatus/WorldStatusPanelContainer';
import { useStableCallback, useRenderLoopDetection } from '../../hooks/hookUtils';

const SIDEBAR_WIDTH_KEY = 'dm-assistant-sidebar-width';
const DEFAULT_SIDEBAR_WIDTH = 350;

interface DashboardViewProps {
  onImageRectChange: (rect: RenderedImageRect | null) => void;
}

const DashboardView: React.FC<DashboardViewProps> = ({ onImageRectChange }) => {
  // Development-time render loop detection for this layout component with resize handling
  useRenderLoopDetection('DashboardView');

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    // Ensure the parsed width is within reasonable bounds
    const initialWidth = savedWidth ? parseInt(savedWidth, 10) : DEFAULT_SIDEBAR_WIDTH;
    return Math.max(250, Math.min(600, initialWidth));
  });
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // useStableCallback ensures handleMouseUp has a stable reference for the event listener.
  // This prevents the need to re-add/remove event listeners when dependencies change.
  const handleMouseUp = useStableCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = 'default';
    document.body.style.userSelect = 'auto';
  });

  // useStableCallback ensures handleMouseMove always accesses the current isResizing state
  // without being recreated. This is important because the callback is attached as a
  // document-level event listener, and recreating it would cause the effect to re-run,
  // potentially missing events during the transition.
  const handleMouseMove = useStableCallback((e: MouseEvent) => {
    if (isResizing) {
      setSidebarWidth(Math.max(250, Math.min(600, e.clientX)));
    }
  });

  // Event listeners can now be added once on mount since the callbacks are stable.
  // The stable callbacks will always access the latest isResizing state.
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Save width to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  const handleResetWidth = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent starting a resize
    setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
  };

  return (
    <div
      className={styles.grid}
      style={{ gridTemplateColumns: `${sidebarWidth}px 5px 1fr`, gridTemplateRows: 'auto 1fr' }}
    >
      <div className={styles.sidebar}>
        <PanelErrorBoundary>
          <WorldStatusPanelContainer />
        </PanelErrorBoundary>
        <PanelErrorBoundary>
          <NewsTicker />
        </PanelErrorBoundary>
        <PanelErrorBoundary>
          <PlotsPanel />
        </PanelErrorBoundary>
        <PanelErrorBoundary>
          <CombatTrackerContainer />
        </PanelErrorBoundary>
      </div>

      <div className={styles.resizer} onMouseDown={handleMouseDown}>
        <Tooltip title="Reset sidebar width">
          <Button
            className={styles.resetButton}
            icon={<BorderInnerOutlined />}
            onClick={handleResetWidth}
          />
        </Tooltip>
      </div>
      <MapPanel>
        <div className={styles.mapControlsHeader}>
          <MapPanel.Controls />
        </div>
        <div className={styles.mapPanel}>
          <MapPanel.Content isResizing={isResizing} onImageRectChange={onImageRectChange} />
        </div>
      </MapPanel>
    </div>
  );
};

export default DashboardView;